"use client";

import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEditorState } from "react-simple-wysiwyg";

interface StyleOption {
  label: string;
  command: string;
  value: string;
  translationKey: string;
}

const styleOptions: StyleOption[] = [
  { label: "Normal text", command: "formatBlock", value: "P", translationKey: "normalText" },
  { label: "Heading 1", command: "formatBlock", value: "H1", translationKey: "heading1" },
  { label: "Heading 2", command: "formatBlock", value: "H2", translationKey: "heading2" },
  { label: "Heading 3", command: "formatBlock", value: "H3", translationKey: "heading3" },
  { label: "Quote", command: "formatBlock", value: "BLOCKQUOTE", translationKey: "quote" },
  { label: "Code", command: "formatBlock", value: "PRE", translationKey: "code" },
];

export function CustomStyleDropdown() {
  const { t } = useTranslation(["common"]);
  const editorState = useEditorState();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { $el, $selection, htmlMode } = editorState;

  if (htmlMode) {
    return null;
  }

  // Find the active style based on the current selection
  const activeOption = styleOptions.find(
    (option) => option.command === "formatBlock" && $selection?.nodeName === option.value,
  );

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  React.useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  const applyStyle = (option: StyleOption) => {
    if (document.activeElement !== $el) {
      $el?.focus();
    }

    document.execCommand(option.command, false, option.value);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className="relative">
      <button
        aria-expanded={isOpen}
        aria-haspopup="true"
        className={`flex h-8 w-fit! items-center justify-center rounded-md px-2 text-sm font-medium transition-colors ${
          isOpen ? "bg-primary/10 text-primary" : "hover:bg-muted text-foreground"
        }`}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
      >
        {activeOption
          ? t(`common:${activeOption.translationKey}`, activeOption.label)
          : t("common:styles", "Styles")}
        <svg
          className="ml-1 h-4 w-4"
          fill="currentColor"
          viewBox="0 0 20 20"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            clipRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            fillRule="evenodd"
          />
        </svg>
      </button>
      {isOpen && (
        <div className="bg-background border-border absolute z-10 mt-1 w-48 rounded-md border shadow-lg">
          <div className="flex w-full flex-col gap-1 py-1" role="menu">
            {styleOptions.map((option) => (
              <button
                key={option.value}
                className={`hover:bg-muted/30! focus:bg-muted w-full! px-4 py-2 text-left text-sm focus:outline-none ${
                  activeOption?.value === option.value ? "bg-primary/10 text-primary" : ""
                }`}
                role="menuitem"
                onClick={() => applyStyle(option)}
              >
                {t(`common:${option.translationKey}`, option.label)}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
